// Bump the corresponding version whenever a legal policy changes. Use
// YYYY-MM-DD.revision so more than one revision can be published in a day.
// Stored acceptances are matched exactly against these values.
export const LEGAL_POLICY_VERSIONS = Object.freeze({
  privacy: "2026-10-18.2",
  security: "2026-10-18",
});

export * from "./mendoza-judicial-catalog.js";

export function legalPolicyEffectiveDate(version) {
  return String(version || "").match(/^\d{4}-\d{2}-\d{2}/)?.[0] || "";
}

export const FILTER_MODES = Object.freeze({
  CUIJ: "cuij",
  JURISDICTION: "jurisdiction",
  SAVED_EXPEDIENTES: "saved_expedientes",
  FOLLOW_UPS: "follow_ups",
  INTEGRATION: "integration",
});

export const INTEGRATION_TYPES = Object.freeze({
  PJM_NOTIFICATIONS: "pjm_notifications",
  PJN_SCW: "pjn_scw",
});

export const INTEGRATION_DEFINITIONS = Object.freeze({
  [INTEGRATION_TYPES.PJM_NOTIFICATIONS]: Object.freeze({
    type: INTEGRATION_TYPES.PJM_NOTIFICATIONS,
    name: "Sistema de Notificaciones del PJM",
    description:
      "Consultá en Praxis las notificaciones recibidas en el Poder Judicial de Mendoza.",
    route: "/integraciones/pjm-notifications",
    navigationLabel: "Notificaciones del PJM",
    icon: "notifications",
    configuredLabel: "Conectada",
  }),
  [INTEGRATION_TYPES.PJN_SCW]: Object.freeze({
    type: INTEGRATION_TYPES.PJN_SCW,
    name: "Consulta de Expedientes del PJN",
    description:
      "Guardá de forma segura tus credenciales del Portal PJN para consultar expedientes federales.",
    route: "/integraciones/pjn-scw",
    navigationLabel: "Expedientes del PJN",
    icon: "judiciary",
    configuredLabel: "Configurada",
  }),
});

export const INTEGRATION_CATALOG = Object.freeze(
  Object.values(INTEGRATION_DEFINITIONS),
);

export const SAVED_EXPEDIENT_MODES = Object.freeze({
  ALL: "all",
  SELECTED: "selected",
});

export const EVENT_TYPES = Object.freeze({
  ANY_MOVEMENT: "any_movement",
  STAGE_CHANGE: "stage_change",
  STUCK: "stuck",
  NEW_INTEGRATION_NOTIFICATION: "new_integration_notification",
});

export const EVENT_LABELS = Object.freeze({
  [EVENT_TYPES.ANY_MOVEMENT]: "Nuevo movimiento",
  [EVENT_TYPES.STAGE_CHANGE]: "Cambio de etapa",
  attention_required: "Cambio de etapa",
  [EVENT_TYPES.STUCK]: "Expediente sin movimiento",
  [EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION]: "Nueva notificación",
});

export const CHANNELS = Object.freeze({
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp",
});

export const CHANNEL_LABELS = Object.freeze({
  [CHANNELS.EMAIL]: "Email",
  [CHANNELS.SMS]: "SMS",
  [CHANNELS.WHATSAPP]: "WhatsApp",
});

export const RECIPIENT_MODES = Object.freeze({
  ALL: "all",
  SELECTED: "selected",
});

export const RULE_STATUSES = Object.freeze({
  ACTIVE: "active",
  PAUSED: "paused",
});

export const ALL_COURTS_ID = "99";
export const CUIJ_PATTERN = /^\d{2}-\d{8}-\d(?:\/\d+)?$/;
export const MENDOZA_CUIJ_PATTERN = /^13-\d{8}-\d(?:\/\d+)?$/;

export const EXPEDIENTE_SOURCE_IDS = Object.freeze({
  MENDOZA: "pjm",
  NATIONAL: "pjn",
});

export function normalizeCuij(value = "") {
  return String(value).replace(/\D/g, "");
}

export function formatCuij(value = "") {
  const digits = normalizeCuij(value);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  const formattedBase =
    `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10, 11)}`;
  const suffix = digits.slice(11);
  return suffix ? `${formattedBase}/${suffix}` : formattedBase;
}

export function isValidCuij(value) {
  return CUIJ_PATTERN.test(String(value));
}

export function isMendozaCuij(value) {
  return MENDOZA_CUIJ_PATTERN.test(String(value));
}

export function parsePjnExpedienteSlug(value) {
  const normalized = String(value || "").trim().toUpperCase();
  const federal = normalized.match(
    /^([A-Z]{3})-0*(\d{1,9})-(\d{4})(?:-(\d+))?$/u,
  );
  if (federal) {
    const continuation = federal[4] || null;
    return {
      kind: "federal",
      jurisdictionPrefix: federal[1],
      number: federal[2],
      year: federal[3],
      continuation,
      docketClass: null,
      tome: null,
      normalized: [
        `${federal[1]} ${federal[2]}/${federal[3]}`,
        continuation,
      ].filter(Boolean).join("/"),
    };
  }

  const supremeCourt = normalized.match(
    /^([A-Z]{1,4})-0*(\d{1,9})-(\d{4})(?:-TOMO-([A-Z0-9-]+))?$/u,
  );
  if (!supremeCourt) return null;
  const tome = supremeCourt[4] || null;
  return {
    kind: "supreme_court",
    jurisdictionPrefix: "CSJ",
    number: supremeCourt[2],
    year: supremeCourt[3],
    continuation: null,
    docketClass: supremeCourt[1],
    tome,
    normalized: `${supremeCourt[1]}. ${supremeCourt[2]}/${supremeCourt[3]}${
      tome ? ` Tomo ${tome}` : ""
    }`,
  };
}

export function parsePjnExpedienteIdentifier(value) {
  const normalized = String(value || "")
    .replace(/\s+/gu, " ")
    .trim()
    .toUpperCase();
  const federal = normalized.match(
    /^(?:([A-Z]{3})\s+)?0*(\d{1,9})\s*\/\s*(\d{4})(?:\s*\/\s*(\d+))?$/u,
  );
  if (federal) {
    const jurisdictionPrefix = federal[1] || "FMZ";
    const continuation = federal[4] || null;
    return {
      kind: "federal",
      jurisdictionPrefix,
      number: federal[2],
      year: federal[3],
      continuation,
      docketClass: null,
      tome: null,
      normalized: [
        `${jurisdictionPrefix} ${federal[2]}/${federal[3]}`,
        continuation,
      ].filter(Boolean).join("/"),
    };
  }

  const supremeCourt = normalized.match(
    /^([A-Z]{1,4})\.\s*0*(\d{1,9})\s*\/\s*(\d{4})(?:\s+TOMO\s+([A-Z0-9-]+))?$/u,
  );
  if (!supremeCourt) return parsePjnExpedienteSlug(normalized);
  const tome = supremeCourt[4] || null;
  return {
    kind: "supreme_court",
    jurisdictionPrefix: "CSJ",
    number: supremeCourt[2],
    year: supremeCourt[3],
    continuation: null,
    docketClass: supremeCourt[1],
    tome,
    normalized: `${supremeCourt[1]}. ${supremeCourt[2]}/${supremeCourt[3]}${
      tome ? ` Tomo ${tome}` : ""
    }`,
  };
}

export function pjnExpedienteSlug(value) {
  const reference = parsePjnExpedienteIdentifier(value);
  if (!reference) return null;
  if (reference.kind === "supreme_court") {
    return [
      reference.docketClass,
      reference.number,
      reference.year,
      ...(reference.tome ? ["TOMO", reference.tome] : []),
    ].join("-");
  }
  return [
    reference.jurisdictionPrefix,
    reference.number,
    reference.year,
    reference.continuation,
  ].filter(Boolean).join("-");
}

export function classifyExpedienteIdentifier(value) {
  const identifier = String(value || "").trim();
  if (isMendozaCuij(identifier)) {
    return {
      sourceId: EXPEDIENTE_SOURCE_IDS.MENDOZA,
      identifier: formatCuij(identifier),
    };
  }
  const national = parsePjnExpedienteSlug(identifier);
  if (national) {
    return {
      sourceId: EXPEDIENTE_SOURCE_IDS.NATIONAL,
      identifier: national.normalized,
      reference: national,
    };
  }
  return null;
}

export function expedienteDetailPath(identifier, section = "") {
  const normalizedIdentifier = String(identifier).trim();
  const routeIdentifier = pjnExpedienteSlug(normalizedIdentifier)
    || normalizedIdentifier;
  const base = `/expedientes/${encodeURIComponent(routeIdentifier)}`;
  const normalizedSection = String(section || "").replace(/^\/+|\/+$/gu, "");
  return normalizedSection ? `${base}/${normalizedSection}` : base;
}

export function encodeCuijPath(value) {
  // The optional continuation suffix is part of the CUIJ, not a route
  // separator. Encoding the complete value keeps `/6` inside one URL segment.
  return encodeURIComponent(formatCuij(value));
}

function pdfDateSegment(value) {
  if (!value) return "";
  const directDate = String(value).match(/^(\d{4}-\d{2}-\d{2})/u)?.[1];
  if (directDate) return directDate;
  const numeric = Number(value);
  const date = new Date(
    Number.isFinite(numeric) && numeric > 0
      ? numeric < 1e12
        ? numeric * 1000
        : numeric
      : value,
  );
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      day: "2-digit",
      month: "2-digit",
      timeZone: "America/Argentina/Mendoza",
      year: "numeric",
    })
      .formatToParts(date)
      .filter(({ type }) => type !== "literal")
      .map(({ type, value: part }) => [type, part]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function pdfFileSegment(value, { fallback = "", uppercase = false } = {}) {
  const segment = String(value || fallback)
    .replace(/\.pdf$/iu, "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/&/gu, " y ")
    .replace(/[^a-zA-Z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .replace(/-{2,}/gu, "-");
  return uppercase ? segment.toUpperCase() : segment;
}

/**
 * Produces a sortable, filesystem-safe PDF name. Legal documents use their
 * CUIJ as context; other products can provide a stable product/notebook name.
 */
export function buildOrganizedPdfFileName({
  attachmentTitle = "",
  category = "DOCUMENTO",
  contextId = "",
  cuij = "",
  date = null,
  maxLength = 180,
  sourceId = "",
  title = "",
} = {}) {
  const fixedSegments = [
    pdfDateSegment(date),
    pdfFileSegment(cuij || contextId),
    pdfFileSegment(category, { fallback: "DOCUMENTO", uppercase: true }),
  ].filter(Boolean);
  const descriptiveSegments = [
    pdfFileSegment(title, { fallback: "Documento" }),
    pdfFileSegment(attachmentTitle),
  ].filter(Boolean);
  const suffix = pdfFileSegment(sourceId);
  const segments = [...fixedSegments, ...descriptiveSegments, suffix]
    .filter(Boolean);
  let fileName = `${segments.join("__")}.pdf`;

  while (fileName.length > maxLength) {
    const candidates = descriptiveSegments
      .map((segment, index) => ({ index, length: segment.length }))
      .filter(({ length }) => length > 18)
      .sort((left, right) => right.length - left.length);
    if (!candidates.length) break;
    const [{ index, length }] = candidates;
    descriptiveSegments[index] = descriptiveSegments[index]
      .slice(0, Math.max(18, length - (fileName.length - maxLength)))
      .replace(/-+$/u, "");
    fileName = `${[
      ...fixedSegments,
      ...descriptiveSegments,
      suffix,
    ].filter(Boolean).join("__")}.pdf`;
  }

  const withoutExtension = fileName.replace(/\.pdf$/iu, "");
  return `${withoutExtension
    .slice(0, maxLength - 4)
    .replace(/[-_]+$/u, "")}.pdf`;
}

export function buildJurisdictionKey(circunscripcionId, typeId, courtId) {
  return `${circunscripcionId}:${typeId}:${courtId}`;
}

export function normalizeApproximateText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("es")
    .replace(/\bp\s*\/\s*/g, "por ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const CANONICAL_SENTENCE_TITLE = /\bsentencia (?:definitiva|oralidad|unipersonal|colegiada|homologatoria|amparo|monitoria)\b/u;
const SENTENCE_REFERENCE_ONLY = [
  /\bautos? para sentencia\b/u,
  /\bllamamiento(?: de)?(?: autos?)? para sentencia\b/u,
  /\bse (?:llamen|dicte)\b.*\bsentencia\b/u,
  /\bsolicit\w*\b.*\bsentencia\b/u,
  /\bconstancia\b.*\bsentencia\b/u,
  /\bnotificaci\w*\b.*\bsentencia\b/u,
  /\bsentencia\b.*\bnotificaci\w*\b/u,
  /\bdeje(?:se)? sin efecto\b.*\bsentencia\b/u,
];

/**
 * Identifies a judgment using only metadata returned with an actuation by the
 * judiciary. It intentionally does not inspect a PDF, extracted text or an AI
 * classification.
 */
export function detectSentenceActuacion(actuacion = {}) {
  const type = normalizeApproximateText(actuacion.tipo);
  const title = normalizeApproximateText(actuacion.titulo);
  const officialSentenceType = type === "sentencia";
  const canonicalTitle = CANONICAL_SENTENCE_TITLE.test(title);
  const referenceOnly = SENTENCE_REFERENCE_ONLY.some((pattern) => pattern.test(title));

  if (officialSentenceType && canonicalTitle) {
    return {
      isSentence: true,
      confidence: "high",
      reason: "judiciary_type_and_canonical_title",
    };
  }
  if (officialSentenceType) {
    return {
      isSentence: true,
      confidence: "probable",
      reason: "judiciary_type",
    };
  }
  if (
    canonicalTitle
    && !referenceOnly
    && !actuacion.esEscrito
  ) {
    return {
      isSentence: true,
      confidence: "probable",
      reason: "canonical_title",
    };
  }
  return {
    isSentence: false,
    confidence: null,
    reason: referenceOnly ? "sentence_reference_only" : "no_sentence_signal",
  };
}

function editDistance(left, right) {
  const previous = Array.from(
    { length: right.length + 1 },
    (_, index) => index,
  );
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1]
          + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
      );
    }
    previous.splice(0, previous.length, ...current);
  }
  return previous[right.length];
}

function approximateTokenMatch(left, right) {
  if (left === right) return true;
  const shortest = Math.min(left.length, right.length);
  const longest = Math.max(left.length, right.length);
  if (shortest >= 4 && (left.startsWith(right) || right.startsWith(left))) {
    return true;
  }
  return longest >= 5 && editDistance(left, right) <= Math.max(1, Math.floor(longest * 0.2));
}

export function approximatelyIncludes(text, pattern) {
  const textTokens = normalizeApproximateText(text).split(" ").filter(Boolean);
  const patternTokens = normalizeApproximateText(pattern)
    .split(" ")
    .filter(Boolean);
  if (!patternTokens.length || patternTokens.length > textTokens.length) {
    return false;
  }
  const lastStart = textTokens.length - patternTokens.length;
  for (let start = 0; start <= lastStart; start += 1) {
    const matches = patternTokens.every((token, offset) =>
      approximateTokenMatch(textTokens[start + offset], token),
    );
    if (matches) return true;
  }
  return false;
}

export function matchesApproximateText(values, patterns) {
  const text = (Array.isArray(values) ? values : [values])
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(" ");
  const queries = (Array.isArray(patterns) ? patterns : [patterns])
    .map((pattern) => String(pattern || "").trim())
    .filter(Boolean);
  return !queries.length || queries.some((pattern) =>
    approximatelyIncludes(text, pattern),
  );
}

const PARTY_ROLE_PATTERNS = Object.freeze({
  actor: [
    /\bactor(?:a|es|as)?\b/,
    /\baccionante(?:s)?\b/,
    /\bdemandante(?:s)?\b/,
    /\breclamante(?:s)?\b/,
  ],
  defendant: [
    /\bdemandad(?:o|a|os|as)\b/,
    /\baccionad(?:o|a|os|as)\b/,
  ],
});

const NAME_STOP_WORDS = new Set([
  "abog",
  "abogada",
  "abogado",
  "dr",
  "dra",
  "doctor",
  "doctora",
  "mat",
  "mp",
  "tomo",
  "folio",
]);

function normalizedNameTokens(value) {
  return normalizeApproximateText(value)
    .split(" ")
    .filter(
      (token) =>
        token &&
        !NAME_STOP_WORDS.has(token) &&
        !/^\d+$/.test(token),
    );
}

function uniqueTokens(value) {
  return [...new Set(normalizedNameTokens(value))];
}

function tokenOverlapScore(left, right) {
  const leftTokens = uniqueTokens(left);
  const rightTokens = uniqueTokens(right);
  if (!leftTokens.length || !rightTokens.length) return 0;
  const rightSet = new Set(rightTokens);
  const matches = leftTokens.filter((token) => rightSet.has(token)).length;
  return matches / Math.min(leftTokens.length, rightTokens.length);
}

function normalizeRegistrationValue(value) {
  return normalizeApproximateText(value).replace(/\s+/g, "");
}

function representativeRegistrations(representative = {}) {
  return [
    representative.matricula,
    representative.numeroMatricula,
    representative.matriculaProfesional,
    representative.registrationNumber,
  ]
    .map(normalizeRegistrationValue)
    .filter(Boolean);
}

function lawyerNames(lawyer = {}) {
  return [lawyer.fullName, ...(lawyer.aliases || [])].filter(Boolean);
}

export function matchRosterLawyer(representative, lawyers = []) {
  const representativeName =
    representative?.nombreApellido ||
    representative?.fullName ||
    representative?.name ||
    "";
  const normalizedRepresentative = uniqueTokens(representativeName);
  const registrations = representativeRegistrations(representative);
  let best = null;

  for (const lawyer of lawyers) {
    const lawyerRegistration = normalizeRegistrationValue(
      lawyer.registration?.number,
    );
    if (
      lawyerRegistration &&
      registrations.includes(lawyerRegistration)
    ) {
      return {
        lawyer,
        confidence: 1,
        method: "registration",
        inferred: false,
      };
    }

    for (const name of lawyerNames(lawyer)) {
      const normalizedLawyer = uniqueTokens(name);
      if (!normalizedLawyer.length || !normalizedRepresentative.length) {
        continue;
      }
      const sameTokens =
        normalizedLawyer.length === normalizedRepresentative.length &&
        normalizedLawyer.every((token) =>
          normalizedRepresentative.includes(token),
        );
      if (sameTokens) {
        return {
          lawyer,
          confidence: 0.99,
          method: "name",
          inferred: false,
        };
      }
      const score = tokenOverlapScore(name, representativeName);
      const sharedTokens = normalizedLawyer.filter((token) =>
        normalizedRepresentative.includes(token),
      ).length;
      if (
        sharedTokens >= 2 &&
        score >= 0.67 &&
        (!best || score > best.confidence)
      ) {
        best = {
          lawyer,
          confidence: score,
          method: "name_partial",
          inferred: true,
        };
      }
    }
  }
  return best;
}

function explicitPartyGroup(link = "") {
  const normalized = normalizeApproximateText(link);
  if (
    PARTY_ROLE_PATTERNS.actor.some((pattern) => pattern.test(normalized))
  ) {
    return "actor";
  }
  if (
    PARTY_ROLE_PATTERNS.defendant.some((pattern) =>
      pattern.test(normalized),
    )
  ) {
    return "defendant";
  }
  return null;
}

function splitCaseCaption(caption = "") {
  const comparable = String(caption || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  const versus = comparable.match(/\s+(?:C\s*[\/.-]|CONTRA)\s*/i);
  if (!versus) return { actor: "", defendant: "" };
  const actor = comparable.slice(0, versus.index);
  const remainder = comparable.slice(
    versus.index + versus[0].length,
  );
  const disposition = remainder.match(
    /\s+(?:P\s*[\/-]|S\s*[\/-]|POR)\s*/i,
  );
  const defendant = disposition
    ? remainder.slice(0, disposition.index)
    : remainder;
  return {
    actor: normalizeApproximateText(actor),
    defendant: normalizeApproximateText(defendant),
  };
}

function inferPartyGroupFromCaption(partyName, captionParts) {
  const actorScore = tokenOverlapScore(partyName, captionParts.actor);
  const defendantScore = tokenOverlapScore(
    partyName,
    captionParts.defendant,
  );
  const bestScore = Math.max(actorScore, defendantScore);
  if (bestScore < 0.6 || actorScore === defendantScore) return null;
  return {
    group: actorScore > defendantScore ? "actor" : "defendant",
    confidence: bestScore,
    source: "caption",
  };
}

export function analyzeExpedienteParties(
  { partes = [], caratula = "" } = {},
  lawyers = [],
) {
  const captionParts = splitCaseCaption(caratula);
  const groups = {
    actor: [],
    defendant: [],
    other: [],
  };

  (partes || []).forEach((party, index) => {
    const explicitGroup = explicitPartyGroup(party.vinculo);
    const captionInference = explicitGroup
      ? null
      : inferPartyGroupFromCaption(
          party.nombreApellido || "",
          captionParts,
        );
    const group = explicitGroup || captionInference?.group || "other";
    const representatives = (party.representantes || []).map(
      (representative) => ({
        ...representative,
        rosterMatch: matchRosterLawyer(representative, lawyers),
      }),
    );
    const partyRosterMatch = matchRosterLawyer(party, lawyers);
    groups[group].push({
      ...party,
      partyKey: party.perId || `${group}:${index}`,
      group,
      groupEvidence: explicitGroup
        ? {
            confidence: 1,
            source: "link",
          }
        : captionInference || {
            confidence: 0,
            source: "unknown",
          },
      rosterMatch: partyRosterMatch,
      representatives,
      hasRosterMatch:
        Boolean(partyRosterMatch) ||
        representatives.some((representative) =>
          Boolean(representative.rosterMatch),
        ),
    });
  });

  return groups;
}

export function representedExpedientePartyGroups(expediente, lawyers = []) {
  const groups = analyzeExpedienteParties(expediente, lawyers);
  return ["actor", "defendant"].filter((group) =>
    groups[group].some(({ hasRosterMatch }) => hasRosterMatch),
  );
}

export function isDeadlineForRepresentedParty(
  deadline,
  representedPartyGroups = [],
) {
  return ["actor", "defendant"].includes(deadline?.dueParty) &&
    representedPartyGroups.includes(deadline.dueParty);
}

const CLOSED_DEADLINE_STATUSES = new Set([
  "completed",
  "completed_inferred",
  "closed_by_progress",
  "expired_unexercised",
  "replaced",
  "historical_reference",
  "pending_conditions",
  "suspended_or_conditioned",
  "needs_review",
  "not_applicable",
]);

const EXTERNAL_DEADLINE_PARTIES = new Set([
  "court",
  "expert",
  "prosecutor",
  "third_party",
  "other",
]);
const TRAFFIC_COLOR_SEVERITY = Object.freeze({
  green: 0,
  yellow: 1,
  red: 2,
});

const MENDOZA_DATE_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Argentina/Mendoza",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function dateOnlyUtc(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return Number.isNaN(date.getTime()) ? null : date;
}

function mendozaTodayUtc(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    MENDOZA_DATE_PARTS.formatToParts(date).map(({ type, value }) => [
      type,
      value,
    ]),
  );
  return new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
  );
}

export function businessDaysUntil(dueDateValue, now = new Date()) {
  const dueDate = dateOnlyUtc(dueDateValue);
  const today = mendozaTodayUtc(now);
  if (!dueDate || !today) return null;
  if (dueDate < today) return -1;

  let count = 0;
  const cursor = new Date(today);
  while (cursor < dueDate) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const weekday = cursor.getUTCDay();
    if (weekday !== 0 && weekday !== 6) count += 1;
  }
  return count;
}

function deadlineIsOpen(deadline) {
  if (deadline?.manualOverride?.status === "completed") return false;
  if (deadline?.lifecycleStatus && deadline.lifecycleStatus !== "open") {
    return false;
  }
  if (
    deadline?.clockStatus &&
    !["running"].includes(deadline.clockStatus)
  ) {
    return false;
  }
  return !CLOSED_DEADLINE_STATUSES.has(deadline?.status);
}

export function deadlineDependsOnUs(
  deadline,
  representedPartyGroups = [],
) {
  const dueParty = String(deadline?.dueParty || "").toLowerCase();
  if (EXTERNAL_DEADLINE_PARTIES.has(dueParty)) return false;
  if (["actor", "defendant"].includes(dueParty)) {
    if (!representedPartyGroups.length) return true;
    return isDeadlineForRepresentedParty(deadline, representedPartyGroups);
  }
  if (["party", "party_or_court"].includes(dueParty)) return true;
  // If the procedural role cannot be resolved, err on the side of attention.
  return true;
}

export function deadlineTrafficColor(
  deadline,
  representedPartyGroups = [],
  { now = new Date() } = {},
) {
  if (!deadlineIsOpen(deadline)) return "green";
  const remaining = Number.isFinite(deadline?.businessDaysRemaining)
    ? deadline.businessDaysRemaining
    : businessDaysUntil(deadline?.dueDate, now);
  const overdue = deadline?.status === "overdue" || remaining < 0;
  const dueToday = deadline?.status === "due_today";

  if (deadlineDependsOnUs(deadline, representedPartyGroups)) {
    if (overdue || dueToday || (remaining !== null && remaining <= 5)) {
      return "red";
    }
    return deadline?.status === "upcoming" ||
      (remaining !== null && remaining > 5)
      ? "yellow"
      : "green";
  }

  if (overdue || dueToday) return "red";
  if (remaining !== null && remaining > 0 && remaining <= 2) return "yellow";
  return "green";
}

export function manualReminderTrafficColor(
  reminder,
  { now = new Date() } = {},
) {
  if (reminder?.completedAt || reminder?.status === "completed") {
    return "green";
  }
  return deadlineTrafficColor(
    {
      ...reminder,
      dueParty: "party",
      status:
        reminder?.status ||
        (String(reminder?.dueDate || "") < MENDOZA_DATE_PARTS.format(now)
          ? "overdue"
          : "upcoming"),
    },
    ["actor"],
    { now },
  );
}

export function worstTrafficColor(colors = []) {
  return colors.reduce(
    (worst, color) =>
      (TRAFFIC_COLOR_SEVERITY[color] ?? -1) >
      (TRAFFIC_COLOR_SEVERITY[worst] ?? -1)
        ? color
        : worst,
    "green",
  );
}

export function expedienteTrafficState({
  deadlines = [],
  manualReminders = [],
  representedPartyGroups = [],
  now = new Date(),
} = {}) {
  const deadlineColors = [
    ...deadlines.map((deadline) =>
      deadlineTrafficColor(deadline, representedPartyGroups, { now })),
    ...manualReminders.map((reminder) =>
      manualReminderTrafficColor(reminder, { now })),
  ];
  return {
    deadlineColor: worstTrafficColor(deadlineColors),
    expedienteColor: worstTrafficColor(deadlineColors),
  };
}

export function trafficColorToTone(color) {
  return { red: "danger", yellow: "warning", green: "success" }[color] ||
    "success";
}

export function isUrgentDeadline(reminder, now = new Date()) {
  if (!reminder || CLOSED_DEADLINE_STATUSES.has(reminder.status)) return false;
  if (["overdue", "due_today"].includes(reminder.status)) return true;
  const remaining = businessDaysUntil(reminder.dueDate, now);
  return remaining !== null && remaining <= 5;
}

export function deadlineReminderTone(
  reminder,
  { isUserParty = Boolean(reminder?.isUserParty), now = new Date() } = {},
) {
  if (!reminder) return "none";
  if (!isUrgentDeadline(reminder, now)) return "success";
  return isUserParty ? "danger" : "warning";
}

function compareDateOnly(leftValue, rightValue, direction = "asc") {
  const left = /^\d{4}-\d{2}-\d{2}$/.test(String(leftValue || ""))
    ? String(leftValue)
    : null;
  const right = /^\d{4}-\d{2}-\d{2}$/.test(String(rightValue || ""))
    ? String(rightValue)
    : null;
  if (left && right && left !== right) {
    return direction === "desc"
      ? right.localeCompare(left)
      : left.localeCompare(right);
  }
  if (left !== right) return left ? -1 : 1;
  return 0;
}

export function compareDeadlineReminders(
  left,
  right,
  { representedPartyGroups = [], now = new Date() } = {},
) {
  const toneOrder = { danger: 0, warning: 1, success: 2 };
  const leftTone = deadlineReminderTone(left, {
    isUserParty: isDeadlineForRepresentedParty(
      left,
      representedPartyGroups,
    ),
    now,
  });
  const rightTone = deadlineReminderTone(right, {
    isUserParty: isDeadlineForRepresentedParty(
      right,
      representedPartyGroups,
    ),
    now,
  });
  const toneDifference =
    (toneOrder[leftTone] ?? 9) - (toneOrder[rightTone] ?? 9);
  if (toneDifference) return toneDifference;

  const dateDifference = leftTone === "success"
    ? compareDateOnly(left.startDate, right.startDate, "desc")
    : compareDateOnly(left.dueDate, right.dueDate);
  if (dateDifference) return dateDifference;
  return String(left.id || left.label || "").localeCompare(
    String(right.id || right.label || ""),
  );
}

export * from "./mentis/api.js";
export * from "./mentis/domain.js";
export * from "./ai-config.js";
export * from "./access-catalog.js";
export * from "./activity-catalog.js";
export * from "./citation-format.js";
